import React, {
  useRef,
  createContext,
  useContext,
  useEffect,
  useState,
  useMemo,
} from "react";
import { type BagResponseV4 } from "@repo/apis/bag";
import { StockPriceData, getStockPriceData } from "@repo/apis/stockprice";
import { initialiseBagSdk } from "@repo/bag";
import type { BagSdkInstance, BagSdkConfig, BagState } from "@repo/bag";
import { ChameleonRequest } from "@repo/chameleon-request";
import { useAuth } from "@repo/ui/auth";
import { useStore } from "@repo/ui/store";

type AddProduct = (params: { variantId: number }) => Promise<void>;
type RemoveProduct = (params: { bagItemId: string }) => Promise<void>;
type UpdateProduct = (params: {
  variantId: number;
  quantity: number;
  bagItemId: string;
}) => Promise<BagResponseV4>;

export type BagLoadingStates = {
  isAddingProduct: boolean;
  isRemovingProduct: boolean;
  isUpdatingProduct: boolean;
  removingItemIds: Record<string, boolean>;
  updatingItemIds: Record<string, boolean>;
};

export type BagContextState =
  | {
      status: "initialising";
      addProduct: AddProduct;
      removeProduct: RemoveProduct;
      updateProduct: UpdateProduct;
      customerHasBag: null;
      getTotalPrice: null;
      getItemCount: null;
      getProducts: null;
      initialiseCheckout: null;
      deliveryInformation: null;
      getSubtotalPrice: null;
      bagLoadingStates: BagLoadingStates;
      isBagUpdating: boolean;
      getStockPrice: (productId: number) => StockPriceData | undefined;
      fetchAndSetStockPriceMap: () => Promise<void>;
      getNextDayDelivery?: null;
      getFreeDeliveryQualification?: null;
    }
  | {
      status: "error";
      addProduct: AddProduct;
      removeProduct: RemoveProduct;
      updateProduct: UpdateProduct;
      customerHasBag: null;
      getTotalPrice: null;
      getItemCount: null;
      getProducts: null;
      initialiseCheckout: null;
      deliveryInformation: null;
      getSubtotalPrice: null;
      bagLoadingStates: BagLoadingStates;
      isBagUpdating: boolean;
      getStockPrice: (productId: number) => StockPriceData | undefined;
      fetchAndSetStockPriceMap: () => Promise<void>;
      getNextDayDelivery?: null;
      getFreeDeliveryQualification?: null;
    }
  | {
      status: "ready";
      addProduct: AddProduct;
      removeProduct: RemoveProduct;
      updateProduct: UpdateProduct;
      getTotalPrice: BagSdkInstance["getTotalPrice"];
      getItemCount: BagSdkInstance["getItemCount"];
      getProducts: BagSdkInstance["getProducts"];
      customerHasBag: boolean;
      initialiseCheckout: BagSdkInstance["initialiseCheckout"];
      deliveryInformation: NonNullable<BagState>["delivery"] | null;
      getSubtotalPrice: BagSdkInstance["getSubtotalPrice"];
      bagLoadingStates: BagLoadingStates;
      isBagUpdating: boolean;
      getStockPrice: (productId: number) => StockPriceData | undefined;
      fetchAndSetStockPriceMap: () => Promise<void>;
      getNextDayDelivery?: BagSdkInstance["getNextDayDelivery"];
      getFreeDeliveryQualification?: BagSdkInstance["getFreeDeliveryQualification"];
    };

type DeferredBagSdk = {
  promise: Promise<BagSdkInstance>;
  resolve: (sdk: BagSdkInstance) => void;
};

export type BagProviderProps = {
  children: React.ReactNode;
  chameleonRequestInstance: ChameleonRequest;
  bagExpansions: BagSdkConfig["initialBagExpansions"];
};

const defaultValue: BagContextState = {
  status: "initialising",
  addProduct: async () => {},
  removeProduct: async () => {},
  updateProduct: async () => ({}) as BagResponseV4,
  getTotalPrice: null,
  getItemCount: null,
  getProducts: null,
  customerHasBag: null,
  deliveryInformation: null,
  initialiseCheckout: null,
  getSubtotalPrice: null,
  getNextDayDelivery: null,
  getFreeDeliveryQualification: null,
  bagLoadingStates: {
    isAddingProduct: false,
    isRemovingProduct: false,
    isUpdatingProduct: false,
    removingItemIds: {},
    updatingItemIds: {},
  },
  isBagUpdating: false,
  getStockPrice: () => undefined,
  fetchAndSetStockPriceMap: async () => {},
};

const BagContext = createContext<BagContextState>(defaultValue);

export const Provider = ({
  chameleonRequestInstance,
  bagExpansions,
  children,
}: BagProviderProps) => {
  const [bagSdk, setBagSdk] = useState<BagSdkInstance | null>(null);
  const [status, setStatus] =
    useState<BagContextState["status"]>("initialising");
  const [bag, setBag] = useState<BagState>(null);
  const [bagLoadingStates, setBagLoadingStates] = useState<
    BagContextState["bagLoadingStates"]
  >(defaultValue.bagLoadingStates);
  const [stockPriceMap, setStockPriceMap] = useState<
    Record<number, StockPriceData>
  >({});
  const { status: authStatus, authClient } = useAuth();
  const { countryData, keyStoreDataversion, shop } = useStore();

  const deferredBagSdk = useRef<DeferredBagSdk | null>(null);
  const isInitialRender = useRef(true);
  const lastBagExpansions = useRef<string>("");

  if (!deferredBagSdk.current) {
    let resolver: DeferredBagSdk["resolve"] | null = null;

    const promise = new Promise<BagSdkInstance>((resolve) => {
      resolver = resolve;
    });

    deferredBagSdk.current = {
      promise,
      resolve: resolver!,
    };
  }

  useEffect(() => {
    if (authStatus === "initialising") {
      return;
    }

    const customerId = authClient.getIdTokenClaims().customerId;

    initialiseBagSdk({
      chameleonRequestInstance,
      lang: countryData.defaultLanguage,
      keyStoreDataversion,
      store: countryData.storeCode,
      country: countryData.countryCode,
      originCountry: countryData.countryCode,
      sizeSchema: countryData.defaultSizeSchema,
      currency: countryData.defaultCurrency,
      initialBagExpansions: bagExpansions,
      shop,
      customerId,
      getAuthHeaderPredicate: authClient.getAuthHeaderPayload,
    })
      .then(async (sdk) => {
        setBagSdk(sdk);
        setBag(sdk.initialBag);
        setStatus("ready");
        deferredBagSdk.current?.resolve(sdk);
      })
      .catch(() => {
        setStatus("error");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    countryData,
    keyStoreDataversion,
    shop,
    chameleonRequestInstance,
    authStatus,
    authClient,
  ]);

  /**
   * Update bag expansions when they change after initial render
   * Avoid duplicating requests during initial bag SDK setup
   */
  useEffect(() => {
    if (isInitialRender.current) {
      isInitialRender.current = false;
      lastBagExpansions.current = JSON.stringify(bagExpansions);
      return;
    }

    if (bagSdk && status === "ready") {
      const currentExpansions = JSON.stringify(bagExpansions);

      if (currentExpansions !== lastBagExpansions.current) {
        lastBagExpansions.current = currentExpansions;

        bagSdk
          .updateBagWithExpansions?.({ expansions: bagExpansions })
          .then(async (updatedBag) => {
            setBag(updatedBag);
          });
      }
    }
  }, [bagExpansions, bagSdk, status]);

  const isBagUpdating = useMemo(() => {
    return (
      bagLoadingStates.isAddingProduct ||
      bagLoadingStates.isRemovingProduct ||
      bagLoadingStates.isUpdatingProduct
    );
  }, [bagLoadingStates]);

  const fetchAndSetStockPriceMap = async () => {
    if (!bagSdk) return;

    const products = bagSdk.getProducts?.() || [];
    const productIds = products.map((item) => item.productId);

    if (!productIds.length) return;

    const data = await getStockPriceData({
      requestInstance: chameleonRequestInstance,
      productIds,
      keyStoreDataversion,
      store: countryData.storeCode,
      country: countryData.countryCode,
      lang: countryData.defaultLanguage,
      shop,
      sizeSchema: countryData.defaultSizeSchema,
    });

    const map = (data || []).reduce(
      (acc, sp) => {
        acc[sp.productId] = sp;
        return acc;
      },
      {} as Record<number, StockPriceData>,
    );

    setStockPriceMap(map);
  };

  const addProduct = async (params: { variantId: number }) => {
    const sdk = await deferredBagSdk.current?.promise;

    if (!sdk) {
      throw new Error(
        "Failed to add product to the bag. The Bag SDK did not initialise",
      );
    }

    setBagLoadingStates((prev) => ({ ...prev, isAddingProduct: true }));

    try {
      const updatedBag = await sdk.addProduct(params);
      setBag(updatedBag);
    } finally {
      setBagLoadingStates((prev) => ({ ...prev, isAddingProduct: false }));
    }
  };

  const removeProduct = async (params: { bagItemId: string }) => {
    const sdk = await deferredBagSdk.current?.promise;

    if (!sdk) {
      throw new Error(
        "Failed to remove product from the bag. The Bag SDK did not initialise",
      );
    }

    setBagLoadingStates((prev) => ({
      ...prev,
      isRemovingProduct: true,
      removingItemIds: { ...prev.removingItemIds, [params.bagItemId]: true },
    }));

    try {
      const updatedBag = await sdk.removeProduct(params);
      setBag(updatedBag);
    } finally {
      setBagLoadingStates((prev) => {
        const remainingIds = { ...prev.removingItemIds };
        delete remainingIds[params.bagItemId];
        return {
          ...prev,
          isRemovingProduct: Object.keys(remainingIds).length > 0,
          removingItemIds: remainingIds,
        };
      });
    }
  };

  const updateProduct: UpdateProduct = async (params) => {
    const sdk = await deferredBagSdk.current?.promise;

    if (!sdk) {
      throw new Error(
        "Failed to update product in the bag. The Bag SDK did not initialise",
      );
    }

    setBagLoadingStates((prev) => ({
      ...prev,
      isUpdatingProduct: true,
      updatingItemIds: { ...prev.updatingItemIds, [params.bagItemId]: true },
    }));

    try {
      const updatedBag = await sdk.updateProduct(params);
      setBag(updatedBag.bag);

      return updatedBag;
    } finally {
      setBagLoadingStates((prev) => {
        const remainingIds = { ...prev.updatingItemIds };
        delete remainingIds[params.bagItemId];

        return {
          ...prev,
          isUpdatingProduct: Object.keys(remainingIds).length > 0,
          updatingItemIds: remainingIds,
        };
      });
    }
  };

  const getStockPrice = (productId: number) => stockPriceMap[productId];

  const contextValue: BagContextState =
    status === "ready" && bagSdk
      ? {
          status,
          customerHasBag: !!bag,
          addProduct,
          removeProduct,
          updateProduct,
          getTotalPrice: bagSdk.getTotalPrice,
          getItemCount: bagSdk.getItemCount,
          getProducts: bagSdk.getProducts,
          initialiseCheckout: bagSdk.initialiseCheckout,
          deliveryInformation: bag?.delivery || null,
          getSubtotalPrice: bagSdk.getSubtotalPrice,
          bagLoadingStates,
          isBagUpdating,
          getStockPrice,
          fetchAndSetStockPriceMap,
          getNextDayDelivery: bagSdk.getNextDayDelivery,
          getFreeDeliveryQualification: bagSdk.getFreeDeliveryQualification,
        }
      : {
          ...defaultValue,
          status: status as "initialising" | "error",
          addProduct,
          removeProduct,
          updateProduct,
          bagLoadingStates,
          getStockPrice,
          fetchAndSetStockPriceMap,
        };

  return (
    <BagContext.Provider value={contextValue}>{children}</BagContext.Provider>
  );
};

Provider.displayName = "Bag.Provider";

export const useBag = () => useContext(BagContext);
